import React from 'react';
import { Box, Typography, Divider, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { QRCodeSVG } from 'qrcode.react';

export interface PassRecord {
  pass_type: string;
  full_name?: string;
  id_number?: string;
  country_of_origin?: string;
  screening_type?: string;
  screening_status?: string;
  action_required?: string;
  next_step?: string;
  issued_at?: string;
  valid_until?: string;
  id?: number;
  screening_id?: number;
  pass_token?: string;
}

interface Props {
  passRecord: PassRecord;
}

const fmt = (iso?: string) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
};

const fmtReportId = (id?: number, issuedAt?: string) => {
  if (!id) return null;
  const d = issuedAt ? new Date(issuedAt) : new Date();
  const yy  = d.getFullYear();
  const mm  = String(d.getMonth() + 1).padStart(2, '0');
  const dd  = String(d.getDate()).padStart(2, '0');
  const seq = String(id).padStart(4, '0');
  return `EVD-${yy}${mm}${dd}-${seq}`;
};

const HealthPassCard: React.FC<Props> = ({ passRecord }) => {
  const isCleared  = passRecord.pass_type === 'cleared';
  const accent     = isCleared ? '#1a7a3c' : '#8b0000';
  const accentLight = isCleared ? '#e8f5e9' : '#fce4e4';
  const reportId = fmtReportId(passRecord.screening_id, passRecord.issued_at);

  const rows: [string, string][] = [
    ['Full Name',             passRecord.full_name          ?? '—'],
    ['Passport Number',       passRecord.id_number          ?? '—'],
    ['Country of Origin',     passRecord.country_of_origin  ?? '—'],
    ['Screening Type',        passRecord.screening_type     ?? 'Ebola Screening'],
    ['Screening Status',      passRecord.screening_status   ?? '—'],
    ...(isCleared
      ? [
          ['Date Issued', fmt(passRecord.issued_at)],
          ['Valid Until',  fmt(passRecord.valid_until)],
        ] as [string, string][]
      : [
          ['Action Required', passRecord.action_required ?? 'Report to health checkpoint'],
          ['Next Step',       passRecord.next_step       ?? 'Await clinician review'],
        ] as [string, string][]
    ),
  ];

  return (
    <Box
      id="health-pass-card"
      sx={{
        border: `2px solid ${accent}`,
        borderRadius: 2,
        overflow: 'hidden',
        maxWidth: 580,
        mx: 'auto',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        fontFamily: '"Roboto", sans-serif',
        bgcolor: '#fff',
      }}
    >
      {/* ── Header strip ── */}
      <Box sx={{ bgcolor: accent, px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.7rem', letterSpacing: 1, textTransform: 'uppercase' }}>
            République Centrafricaine 🇨🇫
          </Typography>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem', letterSpacing: 0.5 }}>
            Voyage Santé+ · Ministère de la Santé
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          {isCleared
            ? <CheckCircleIcon sx={{ color: '#fff', fontSize: 32, opacity: 0.95 }} />
            : <CancelIcon     sx={{ color: '#fff', fontSize: 32, opacity: 0.95 }} />
          }
        </Box>
      </Box>

      {/* ── Sub-header ── */}
      <Box sx={{ bgcolor: accentLight, px: 2.5, py: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: accent, textTransform: 'uppercase', letterSpacing: 0.8, flex: 1 }}>
          Traveller Health Pass — Ebola Screening Certificate
        </Typography>
        {reportId && (
          <Chip
            label={reportId}
            size="small"
            sx={{ bgcolor: accent, color: '#fff', fontWeight: 700, fontSize: '0.72rem', letterSpacing: 0.5 }}
          />
        )}
      </Box>

      {/* ── Status banner ── */}
      <Box sx={{ bgcolor: accent, py: 0.6, textAlign: 'center' }}>
        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.8rem', letterSpacing: 2, textTransform: 'uppercase' }}>
          {isCleared ? '✓  Cleared for Travel' : '✗  Not Cleared — Further Assessment Required'}
        </Typography>
      </Box>

      {/* ── Body: fields + QR ── */}
      <Box sx={{ display: 'flex', gap: 2, px: 2.5, pt: 2, pb: 1.5 }}>

        {/* Field table */}
        <Box sx={{ flex: 1 }}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {rows.map(([label, value]) => (
                <Box component="tr" key={label} sx={{ borderBottom: '1px solid #eee' }}>
                  <Box component="td" sx={{
                    py: 0.7, pr: 1.5,
                    fontSize: '0.72rem',
                    color: '#666',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    width: '38%',
                    verticalAlign: 'top',
                    whiteSpace: 'nowrap',
                  }}>
                    {label}
                  </Box>
                  <Box component="td" sx={{ py: 0.7, fontSize: '0.85rem', fontWeight: 700, color: '#1a1a1a' }}>
                    {value}
                  </Box>
                </Box>
              ))}
            </tbody>
          </Box>
        </Box>

        {/* QR code */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, minWidth: 110 }}>
          <Box sx={{
            p: 1,
            borderRadius: 1.5,
            border: `2px solid ${accent}`,
            bgcolor: isCleared ? 'rgba(26,122,60,0.06)' : 'rgba(139,0,0,0.06)',
          }}>
            <QRCodeSVG
              value={
                passRecord.pass_token
                  ? `${window.location.origin}/pass/${passRecord.pass_token}`
                  : `${window.location.origin}/dashboard/traveler-screening/${passRecord.screening_id ?? passRecord.id ?? ''}`
              }
              size={90}
              fgColor={accent}
              bgColor="#ffffff"
              level="H"
            />
          </Box>
          <Typography sx={{ fontSize: '0.6rem', color: '#888', textAlign: 'center', maxWidth: 90 }}>
            Scan to verify pass
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* ── Footer ── */}
      <Box sx={{ px: 2.5, py: 1, bgcolor: '#fafafa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontSize: '0.65rem', color: '#999' }}>
          Issued by: CAR-Ebola AI Connect
          {reportId ? `  ·  Ref: ${reportId}` : ''}
        </Typography>
        <Typography sx={{ fontSize: '0.65rem', color: '#999' }}>
          Valid only for the screening date shown · Not transferable
        </Typography>
      </Box>
    </Box>
  );
};

export default HealthPassCard;
